import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  isTerminalDeactivationStatus,
  type DeactivationStatusResponse,
} from '../../api/organizationStatus';
import { useDeactivationStatus } from '../../hooks/client/useOrganizationStatus';

interface ActiveDeactivation {
  requestId: string;
  orgId: string;
  orgName: string;
}

const STORAGE_KEY = 'speculo:active-deactivation';

const loadFromStorage = (): ActiveDeactivation | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ActiveDeactivation) : null;
  } catch {
    return null;
  }
};

const saveToStorage = (value: ActiveDeactivation | null) => {
  try {
    if (value) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* sessionStorage can be disabled — tracker still works in-memory */
  }
};

interface DeactivationTrackerValue {
  active: ActiveDeactivation | null;
  status: DeactivationStatusResponse | undefined;
  isPolling: boolean;
  isDrawerOpen: boolean;
  isTerminal: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  startTracking: (input: ActiveDeactivation) => void;
  dismiss: () => void;
}

const DeactivationTrackerContext = createContext<DeactivationTrackerValue | null>(null);

interface DeactivationTrackerProviderProps {
  children: React.ReactNode;
  /** Called when the in-flight deactivation reaches a terminal state. Use to refetch the org list. */
  onTerminal?: (data: DeactivationStatusResponse, active: ActiveDeactivation) => void;
}

export const DeactivationTrackerProvider: React.FC<DeactivationTrackerProviderProps> = ({
  children,
  onTerminal,
}) => {
  const [active, setActive] = useState<ActiveDeactivation | null>(() => loadFromStorage());
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [terminalNotified, setTerminalNotified] = useState<string | null>(null);

  const { data: status, isFetching } = useDeactivationStatus(active?.requestId);

  useEffect(() => {
    saveToStorage(active);
  }, [active]);

  useEffect(() => {
    if (!active || !status) return;
    if (!isTerminalDeactivationStatus(status.status)) return;
    if (terminalNotified === active.requestId) return;
    setTerminalNotified(active.requestId);
    onTerminal?.(status, active);
  }, [status, active, terminalNotified, onTerminal]);

  const startTracking = useCallback((input: ActiveDeactivation) => {
    setActive(input);
    setIsDrawerOpen(false);
    setTerminalNotified(null);
  }, []);

  const dismiss = useCallback(() => {
    setActive(null);
    setIsDrawerOpen(false);
    setTerminalNotified(null);
  }, []);

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  const value = useMemo<DeactivationTrackerValue>(
    () => ({
      active,
      status,
      isPolling: isFetching,
      isDrawerOpen,
      isTerminal: !!status && isTerminalDeactivationStatus(status.status),
      openDrawer,
      closeDrawer,
      startTracking,
      dismiss,
    }),
    [active, status, isFetching, isDrawerOpen, openDrawer, closeDrawer, startTracking, dismiss]
  );

  return (
    <DeactivationTrackerContext.Provider value={value}>
      {children}
    </DeactivationTrackerContext.Provider>
  );
};

export const useDeactivationTracker = (): DeactivationTrackerValue => {
  const ctx = useContext(DeactivationTrackerContext);
  if (!ctx) {
    throw new Error('useDeactivationTracker must be used inside <DeactivationTrackerProvider>');
  }
  return ctx;
};
